import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Wrench, MapPin, Clock, AlertTriangle, CheckCircle2, HelpCircle } from "lucide-react";
import { instantTenantDay, todayCalendarDate } from "../../lib/calendar-date";
import { useTenantTimeZone } from "../../hooks/useTenantTimeZone";
import { LoadFailureNotice } from "../ui/LoadFailureNotice";
import type { LoadFailureKind } from "../../lib/load-failure";

interface EquipmentItem {
  id: string;
  name?: string;
  serial_number?: string;
  unit_number?: string;
  unitNumber?: string;
  type?: string;
  status: string;
  last_known_rig?: string;
  lastKnownRig?: string;
  next_inspection_date?: string;
  nextInspectionDate?: string;
}

interface EquipmentDeploymentProps {
  equipment: EquipmentItem[];
  /**
   * Set when the equipment read never arrived. "No equipment found" is a fact
   * about the fleet, and a failed read knows nothing about the fleet.
   */
  failure?: LoadFailureKind | null;
}

/**
 * Saturated signal fills with dark ink, five hues and no purple: Reserved and
 * In Use both mean committed to a job, so they share blue and the label tells
 * them apart (as on the Equipment register).
 *
 * AN UNKNOWN STATUS IS SLATE AND READS "—". This used to fall back to the
 * `available` entry, so any status the screen could not classify rendered as
 * "Yard" — a confident claim that the unit is free to send out.
 */
const statusConfig: Record<string, { label: string; icon: any; fill: string }> = {
  available: { label: "Yard", icon: CheckCircle2, fill: "bg-fi-signal-green" },
  reserved: { label: "Reserved", icon: Clock, fill: "bg-fi-signal-blue" },
  in_use: { label: "In Use", icon: MapPin, fill: "bg-fi-signal-blue" },
  left_on_site: { label: "On Location", icon: AlertTriangle, fill: "bg-fi-signal-amber" },
  maintenance: { label: "Maintenance", icon: Wrench, fill: "bg-fi-signal-red" },
};
const UNKNOWN_STATUS = { label: "\u2014", icon: HelpCircle, fill: "bg-fi-signal-slate" };

const EquipmentDeployment = ({ equipment, failure }: EquipmentDeploymentProps) => {
  const timeZone = useTenantTimeZone();
  const navigate = useNavigate();
  // The tenant's today, the same zone inspDay is read in below.
  const today = todayCalendarDate(timeZone);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.3, ease: "easeOut" }}
    >
      {failure ? (
        <LoadFailureNotice error={null} kind={failure} subject="equipment" className="py-6" />
      ) : equipment.length === 0 ? (
        <p className="py-6 text-center text-sm text-fieldiq-muted">No equipment yet.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {equipment.map((eq, i) => {
            const cfg = statusConfig[eq.status] ?? UNKNOWN_STATUS;
            const StatusIcon = cfg.icon;
            const inspDate = eq.next_inspection_date || eq.nextInspectionDate;
            // An INSTANT (api#838) — the tenant's day it falls on, not its UTC prefix.
            const inspDay = instantTenantDay(inspDate, timeZone);
            const overdue = !!inspDay && !!today && inspDay < today;
            const hasIssue = eq.status === 'left_on_site' || eq.status === 'maintenance' || overdue;
            const location = eq.last_known_rig || eq.lastKnownRig;

            return (
              <motion.button
                key={eq.id}
                onClick={() => navigate(`/equipment/${eq.id}`)}
                className="rounded-fi-control border border-fieldiq-border bg-fieldiq-dark/40 p-3.5 text-left flex items-center gap-3 transition-colors hover:bg-fieldiq-dark/70"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.45 + i * 0.08, duration: 0.4, ease: "easeOut" }}
                whileHover={{ y: -1 }}
              >
                <div className={`w-9 h-9 rounded-fi-control ${cfg.fill} flex items-center justify-center flex-shrink-0`}>
                  <StatusIcon className="w-4 h-4 text-fi-signal-ink" aria-hidden="true" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-fieldiq-text truncate">
                      {eq.name || eq.serial_number || 'Equipment'}
                    </p>
                    <span className="text-[9px] font-mono text-fieldiq-muted">
                      {eq.unit_number || eq.unitNumber || eq.type || ''}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[10px] font-bold text-fieldiq-text">{cfg.label}</span>
                    {location && (
                      <span className="text-[10px] text-fieldiq-muted flex items-center gap-0.5">
                        <MapPin className="w-2.5 h-2.5" /> {location}
                      </span>
                    )}
                  </div>
                  {overdue && (
                    <p className="mt-1 inline-block rounded-fi-pill border border-black/20 bg-fi-signal-red px-1.5 text-[10px] font-bold text-fi-signal-ink">
                      Inspection overdue
                    </p>
                  )}
                </div>
                {hasIssue && (
                  <motion.div
                    className="w-2 h-2 rounded-full bg-fi-signal-amber flex-shrink-0"
                    animate={{ scale: [1, 1.3, 1] }}
                    transition={{ repeat: Infinity, duration: 2 }}
                  />
                )}
              </motion.button>
            );
          })}
        </div>
      )}
    </motion.div>
  );
};

export default EquipmentDeployment;
